import { useState, useEffect, useCallback } from "react";
import { Product } from "../types/product.ts";
import {
  fetchPaginatedProducts,
  ProductsFilters,
} from "../services/paginatedProductService.ts";
import { useToast } from "./use-toast.ts";

interface useServerPaginatedProductProps {
  initialFilters?: ProductsFilters;
  initialItemsPerPage?: number;
}

interface UseServicePaginatedProductReturn {
  products: Product[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startItem: number;
  endItem: number;

  goToPage: (page: number) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  setItemsPerPage: (itemsPerPage: number) => void;
  setFilters: (filters: ProductsFilters) => void;
  refresh: () => void;
}

export const useServerPaginatedProduct = ({
  initialFilters = {},
  initialItemsPerPage = 10,
}: useServerPaginatedProductProps = {}): UseServicePaginatedProductReturn => {
  const [products, setProducts] = useState<Product[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPageState] = useState(initialItemsPerPage);
  const [filters, setFiltersState] = useState<ProductsFilters>(initialFilters);

  const { toast } = useToast();

  const fetchData = useCallback(
    async (page: number = currentPage) => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchPaginatedProducts(
          page,
          itemsPerPage,
          filters
        );
        setProducts(response.products);
        setTotalCount(response.totalCount);
        setTotalPages(response.totalPages);
        setCurrentPage(response.currentPage);
        setTotalItems(response.totalCount);
        setHasMore(response.hasMore);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An unexpected error occurred";
        setError(errorMessage);
        console.error("Error fetching paginated products:", errorMessage);
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    },
    [itemsPerPage, filters, toast]
  );

  useEffect(() => {
    setCurrentPage(1);
    fetchData(1);
  }, [filters, itemsPerPage, fetchData]);

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, fetchData]);

  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const goToPage = useCallback(
    (page: number) => {
      const targetPage = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(targetPage);
    },
    [totalPages]
  );

  const goToNextPage = useCallback(() => {
    if (hasNextPage) {
      setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    }
  }, [hasNextPage]);

  const goToPreviousPage = useCallback(() => {
    if (hasPreviousPage) {
      setCurrentPage((prev) => Math.max(prev - 1, 1));
    }
  }, [hasPreviousPage]);

  const goToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const goToLastPage = useCallback(() => {
    setCurrentPage(totalPages);
  }, [totalPages]);

  const setItemsPerPage = useCallback((newItemsPerPage: number) => {
    setItemsPerPageState(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  const setFilters = useCallback((newFilters: ProductsFilters) => {
    setFiltersState(newFilters);
    setCurrentPage(1);
  }, []);

  const refresh = useCallback(() => {
    fetchData(currentPage);
  }, [currentPage, fetchData]);

  return {
    products,
    totalCount,
    totalPages,
    currentPage,
    hasMore,
    loading,
    error,
    totalItems,
    itemsPerPage,
    hasNextPage,
    hasPreviousPage,
    startItem,
    endItem,

    goToPage,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    setItemsPerPage,
    setFilters,
    refresh,
  };
};
